import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getAgendaAlarmAsync,
  getAlarmQueueAsync,
  getAlarmQueueLastUpdateAsync,
  getAlertCandidatesAsync,
  getDevicesAlarmAsync,
  getMeasureAlarmAsync,
  getOperatorGroupsAsync,
  getPatientAlarmAsync,
  postConsumeAlarmAsync,
} from './alarmQueueThunks';

import { normalizeDataUser } from '../userDetail/operations';
import { normalizeDataMeasure, normalizeDataOperatorGroups } from "./operations";

// Tamaño de página del scroll infinito de la cola: valor inicial, incremento
// por página y valor al que se vuelve al abrir/cerrar el menú.
export const DEFAULT_ALARM_QUEUE_SIZE = 10;

export interface AlarmQueueState {
  items: any[];
  myItems: any[];
  totalSize: number;
  alarmRequestSize: number;
  alarmsQueueOrder: 'asc' | 'desc';
  lastUpdate: number | null;
  pbxStatus: 'UP' | 'RECOVERING' | 'DOWN' | 'NO_PBX' | null;

  polling: boolean;
  pollingError: boolean;
  pollingDelay: { delay: number; max: boolean };

  selectedAlarm: null | any;

  openAlarmResolution: boolean;
  showAlarmResolution: boolean;

  showClientSelection: boolean;
  alertCandidates: any[];
  alertCandidatesStatus: 'idle' | 'loading' | 'failed';

  manualFromMyAlerts: boolean;
  autoAssignmentFromCall: boolean;

  /**
   * Hasta este instante (epoch ms) el listener de polling NO adopta el operatorAlert
   * del backend (salvo autoAssignmentFromCall). Se arma al terminar una atención
   * (aparcar/resolver): una respuesta en vuelo anterior al park aún trae la alerta
   * como atendida y re-engancharía la cabecera.
   */
  adoptionSuppressedUntil: number;

  patientAlarm: null | any;
  measureAlarm: null | any;
  agendaAlarm: null | any;
  devicesAlarm: null | any;

  operatorGroups: null | any;
  operatorGroupsEdited: null | any;
  operatorGroupsStatus: null | 'idle' | 'loading' | 'failed';
}

const initialState: AlarmQueueState = {
  items: [],
  myItems: [],
  totalSize: 0,
  alarmRequestSize: DEFAULT_ALARM_QUEUE_SIZE,
  alarmsQueueOrder: 'desc',
  lastUpdate: null,
  pbxStatus: null,

  polling: false,
  pollingError: false,
  pollingDelay: { delay: 3000, max: false },

  selectedAlarm: null,

  openAlarmResolution: false,
  showAlarmResolution: false,

  showClientSelection: false,
  alertCandidates: [],
  alertCandidatesStatus: 'idle',

  manualFromMyAlerts: false,
  autoAssignmentFromCall: false,
  adoptionSuppressedUntil: 0,

  patientAlarm: null,
  measureAlarm: null,
  agendaAlarm: null,
  devicesAlarm: null,

  operatorGroups: null,
  operatorGroupsEdited: null,
  operatorGroupsStatus: null,
};

const alarmQueueSlice = createSlice({
  name: 'alarmQueue',
  initialState,
  reducers: {
    toggleAlarmQueue: (state, action) => {
      state.openAlarmResolution = action.payload;

      if (action.payload === false) {
        state.alarmRequestSize = DEFAULT_ALARM_QUEUE_SIZE;
      }
    },
    setManualFromMyAlerts: (state, action) => {
      state.manualFromMyAlerts = action.payload;
    },
    setAutoAssignmentFromCall(state, action) {
      state.autoAssignmentFromCall = action.payload;
    },
    suppressAdoption: (state, action) => {
      state.adoptionSuppressedUntil = action.payload;
    },
    setAlarmRequestSize: (state, action) => {
      state.alarmRequestSize = action.payload;
    },
    setAlarmsQueueOrder: (state, action) => {
      state.alarmsQueueOrder = action.payload;
    },
    startPolling(state) {
      state.polling = true;
    },
    stopPolling(state) {
      state.polling = false;
    },
    setPollingError(state, action) {
      state.pollingError = action.payload;
    },
    setPollingDelay(state, action) {
      state.pollingDelay = action.payload;
    },
    setLastUpdate(state, action: PayloadAction<number>) {
      state.lastUpdate = action.payload;
    },
    showAlarmResolution: (state, action) => {
      state.showAlarmResolution = action.payload;
    },
    showClientSelection: (state, action) => {
      state.showClientSelection = action.payload;
    },
    resetAlertCandidates: (state) => {
      state.alertCandidates = [];
      state.alertCandidatesStatus = 'idle';
      state.showClientSelection = false;
    },
    setAlarmSelected: (state, action) => {
      state.selectedAlarm = action.payload;
    },
    // Refresca las working alerts del operador (p.ej. con el operator.workingAlerts
    // que devuelve el propio close), sin esperar al siguiente poll.
    setWorkingAlerts: (state, action) => {
      state.myItems = action.payload ?? [];
    },
    resetAlarm: (state) => {
      state.selectedAlarm = null;
    },
    clearContextData(state) {
      state.patientAlarm = null;
      state.measureAlarm = null;
      state.agendaAlarm = null;
      state.devicesAlarm = null;
    },
    setOperatorGroupEdited: (state, { payload }: PayloadAction<any>) => {
      state.operatorGroupsEdited = payload;
    },
    setOperatorGroup: (state, { payload }: PayloadAction<any>) => {
      state.operatorGroupsEdited = state.operatorGroupsEdited.map((item: any) => ({
        ...item,
        belongsToGroup: item.group.id == payload.row.group.id ? !item.belongsToGroup : item.belongsToGroup,
      }));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAlarmQueueAsync.fulfilled, (state, action) => {
        state.pollingError = false;

        // Descarta respuestas obsoletas: un fetch lanzado con un tamaño ya
        // superado (p.ej. un poll en vuelo mientras el scroll pedía más) no
        // debe truncar la lista al resolver el último.
        const requestedSize = action.payload.requestedSize ?? state.alarmRequestSize;
        if (requestedSize < state.alarmRequestSize) return;

        state.items = action.payload.items ?? [];
        state.myItems = action.payload.myItems ?? [];
        state.totalSize = action.payload.totalSize ?? 0;
      })
      .addCase(getAlarmQueueAsync.rejected, (state, action) => {
        state.pollingError = true;
      })
      .addCase(getAlarmQueueLastUpdateAsync.fulfilled, (state, action) => {
        if (action.payload?.lastUpdate) {
          state.lastUpdate = action.payload.lastUpdate;
        }
        if (action.payload?.pbxStatus) {
          state.pbxStatus = action.payload.pbxStatus;
        }
        state.pollingError = false;
      })
      .addCase(getAlarmQueueLastUpdateAsync.rejected, (state, action) => {
        state.pollingError = true;
      })
      .addCase(postConsumeAlarmAsync.fulfilled, (state, action) => {
        if (action.payload?.ok) {
          const alarm = action.payload.alarm;

          // Buscar y quitar la alerta de la lista global
          state.items = state.items.filter((a: any) => a.uuid !== alarm.uuid);

          // Añadirla al principio de mis alertas
          state.myItems = [alarm, ...state.myItems];
        }
      })

      .addCase(getAlertCandidatesAsync.pending, (state) => {
        state.alertCandidatesStatus = 'loading';
      })
      .addCase(getAlertCandidatesAsync.fulfilled, (state, action) => {
        state.alertCandidates = action.payload ?? [];
        state.alertCandidatesStatus = 'idle';
      })
      .addCase(getAlertCandidatesAsync.rejected, (state) => {
        state.alertCandidatesStatus = 'failed';
      })
      .addCase(getPatientAlarmAsync.fulfilled, (state, action) => {
        state.patientAlarm = normalizeDataUser(action.payload);
      })
      .addCase(getMeasureAlarmAsync.fulfilled, (state, action) => {
        state.measureAlarm = normalizeDataMeasure(
          action.payload?.response?.data?.item,
          action.meta.arg,
          action.payload?.measureTypeLookup ?? {},
        );
      })
      .addCase(getAgendaAlarmAsync.fulfilled, (state, action) => {
        state.agendaAlarm = action.payload?.data?.data;
      })
      .addCase(getDevicesAlarmAsync.fulfilled, (state, action) => {
        state.devicesAlarm = action.payload?.data?.item.items;
      })
      .addCase(getOperatorGroupsAsync.pending, (state) => {
        state.operatorGroupsStatus = 'loading';
      })
      .addCase(getOperatorGroupsAsync.rejected, (state) => {
        state.operatorGroupsStatus = 'failed';
      })
      .addCase(getOperatorGroupsAsync.fulfilled, (state, action) => {
        state.operatorGroupsStatus = 'idle';
        state.operatorGroups = normalizeDataOperatorGroups(action.payload?.data?.operatorGroups);
      });
  },
});

export const {
  setPollingError,
  setPollingDelay,
  toggleAlarmQueue,
  setManualFromMyAlerts,
  setAutoAssignmentFromCall,
  setAlarmRequestSize,
  setAlarmsQueueOrder,
  startPolling,
  stopPolling,
  setLastUpdate,
  showAlarmResolution,
  showClientSelection,
  resetAlertCandidates,
  setAlarmSelected,
  setWorkingAlerts,
  resetAlarm,
  clearContextData,
  setOperatorGroupEdited,
  setOperatorGroup,
  suppressAdoption,
} = alarmQueueSlice.actions;
export default alarmQueueSlice.reducer;
